
import { ReactNode } from "react";
import { Container } from "@/components/ui/container";
import { CheckCircle } from "lucide-react";
import { cn } from "@/lib/utils";

interface Benefit {
  title: string;
  description: string;
}

interface BenefitSectionProps {
  title: string;
  subtitle?: string;
  description?: string;
  benefits: Benefit[];
  className?: string;
  background?: "white" | "light" | "gradient";
  columns?: 1 | 2;
}

export const BenefitSection = ({
  title,
  subtitle,
  description,
  benefits,
  className,
  background = "white",
  columns = 2
}: BenefitSectionProps) => {
  const getBgClasses = () => {
    switch (background) {
      case "light":
        return "bg-gray-50";
      case "gradient":
        return "bg-gradient-to-b from-purple-50 to-white";
      default:
        return "bg-white";
    }
  };

  return (
    <section className={cn("py-16 md:py-24", getBgClasses(), className)}>
      <Container>
        <div className="text-center mb-12">
          {subtitle && (
            <div className="inline-block px-4 py-2 bg-purple-100 rounded-full text-purple-700 font-medium text-sm mb-4">
              {subtitle}
            </div>
          )}
          <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl mb-6">
            {title}
          </h2>
          {description && (
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {description}
            </p>
          )}
        </div>

        <div className={cn(
          "grid gap-x-8 gap-y-4 max-w-4xl mx-auto",
          columns === 2 ? "sm:grid-cols-2" : "grid-cols-1"
        )}>
          {benefits.map((benefit, index) => (
            <div key={index} className="flex space-x-3">
              <CheckCircle className="h-6 w-6 flex-shrink-0 text-green-500" />
              <div>
                <h3 className="font-medium text-gray-900 mb-1">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </div>
            </div>
          ))}
        </div>
      </Container>
    </section>
  );
};
